import { detectBrowser, detectAppVsBrowser, detectDevice } from './newRelicHelpers';

export const newRelicEvents = {
    pageLoad: {
        _event_name: 'page_load',
        homePage: 'Home Page',
        categoryPage: 'Category Page',
        faqArticle: 'FAQ Article',
        troubleshootingQuestion: 'Troubleshooting Question',
        troubleshootingArticle: 'Troubleshooting Step Article',
        searchResultsPage: 'Search Results',
        searchResultsDropdown: 'Search Results Dropdown - Home',
        detachedArticle: 'Single Article',
        hardshipForm: 'Hardship Form',
        raiseComplaint: 'Raise Complaint Form',
    },
    userEvents: {
        enquirySubmit: 'Enquiry Submit',
        _event_name: 'user_event',
        searchEnter: 'Search - Enter',
        searchApiCall: 'Search Api Call',
        liveChat: 'Live Chat',
        contactUs: 'Contact Us',
        popularLink: 'Popular Link',
        quickLink: 'Quick Link',
        articleAction: 'Article Action',
        hardshipSubmit: 'Hardship form - Submit',
        complaintSubmit: 'Complaint form - Submit',
        articleHyperlink: 'Article Hyperlink',
        selectErrorCode: 'Error Code',
        explore: 'Explore',
        breadcrumb: 'Breadcrumb',
        homeCategoryClick: 'Home Category Click'
    },
    apiCall: {
        _event_name: 'apiCall',
        getArticleById: 'get article by id',
    },
};

/**
 * Internal helper that wraps the New Relic Browser API call.
 *
 * @private
 * @param {string} name - The name of the action to record
 * @param {Object} attributes - Custom attributes for the event
 * @returns {void}
 */
const logEvent = (name, attributes) => window.newrelic?.addPageAction(name, attributes);

/**
 * Internal helper that logs errors to New Relic.
 *
 * @private
 * @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/noticeerror-browser-agent-api/
 * @param {Error} error - The error object to log
 * @param {Object} attributes - Custom attributes for the error
 * @returns {void}
 */
const logError = (error, attributes) => window.newrelic?.noticeError(error, attributes);

/**
 * Logs a custom page action event to New Relic with standardized attributes.
 *
 * This function wraps the New Relic Browser API call `newrelic.addPageAction`
 * to send a PageAction event with a name and custom attributes. It automatically
 * includes mandatory attributes like kenanId, browser, environment, etc.
 *
 * Use this to record user interactions or other events that the New Relic
 * Browser agent would not automatically capture.
 *
 * See New Relic documentation for `addPageAction` for more details:
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/add-page-action/
 *
 * @param {Object} params - The event parameters
 * @param {string} params.eventName - The name of the event to record. This will be
 *   reported in New Relic as the `actionName` attribute on the PageAction event.
 * @param {Object} [params.attributes={}] - A set of key/value pairs to include with
 *   the event as custom attributes. These will be stored on the PageAction event
 *   and can be used in NRQL queries (e.g., filtering or faceting).
 * @param {number|string} [params.attributes.status] - Optional status code associated
 *   with the event (e.g., HTTP response code).
 * @param {string} [params.attributes.apiName] - Optional identifier for the backend
 *   API that was called.
 * @param {string} [params.attributes.apiUrl] - Optional backend API endpoint URL
 *   that was called.
 * @param {string} [params.attributes.search] - Optional Search query entered by user.
 * @param {string} [params.attributes.popularLink] - Optional popularLink 
 * (clicked by user in popular topics in home page).
 * @param {string} [params.attributes.linkClicked] - Optional which link user clicked 
 * @param {string} [params.attributes.linkType] - Optional ['internal', 'externa']
 * @param {*} [params.attributes.*] - Other custom attributes can also be provided
 *   and will be forwarded to New Relic alongside the event.
 *
 * @returns {void}
 *
 * @example
 * // Log a simple page load event
 * logNewRelicEvent({
 *   eventName: 'page_load',
 *   attributes: { pageType: 'Home Page' }
 * });
 *
 * @example
 * // Log a user interaction event
 * logNewRelicEvent({
 *   eventName: 'user_event',
 *   attributes: {
 *     action: 'Search - Enter',
 *     searchTerm: 'help'
 *   }
 * });
 */
export default function logNewRelicEvent({ eventName = '', attributes = {} }) {
    const pageUrl = window.location.href;

    // mandatory data for all logs
    const kenanId = window?.foxtel?.user?.accountId || 'anonymous';
    const browser = detectBrowser().browser;
    const appVsBrowser = detectAppVsBrowser();
    const env = import.meta.env.VITE_BOT_ENV;
    const brand = 'foxtel';
    const geoCountry = 'AU';
    const pageLoad = newRelicEvents.pageLoad
    const page = (() => {
        if (pageUrl.includes('sub-section')) return pageLoad.faqArticle;
        if (pageUrl.includes('category')) return pageLoad.categoryPage;
        if (pageUrl.includes('troubleshooting')) return pageLoad.troubleshootingQuestion;
        if (pageUrl.includes('troubleshooting') && pageUrl.includes('article'))
            return pageLoad.troubleshootingArticle;
        if (pageUrl.includes('search-results')) return pageLoad.searchResultsPage;
        if (pageUrl.includes('complaint')) return pageLoad.raiseComplaint;
        if (pageUrl.includes('hardship-form')) return pageLoad.hardshipForm;
    })();

    logEvent(eventName, {
        ...attributes,
        pageUrl,
        kenanId,
        browser,
        appVsBrowser,
        env,
        brand,
        geoCountry,
        pageName: page,
        deviceType: detectDevice().device
    });
}

export function logNewRelicError({ errorName = '', error = {}, attributes = {} }) {
    const pageUrl = window.location.href;

    // mandatory data for all logs
    const kenanId = window?.foxtel?.accountId || 'anonymous';
    const browser = detectBrowser().browser;
    const appVsBrowser = detectAppVsBrowser();
    const env = import.meta.env.VITE_BOT_ENV;
    const brand = 'foxtel';
    const geoCountry = 'AU';

    logError(error, {
        ...attributes,
        errorName,
        pageUrl,
        kenanId,
        browser,
        appVsBrowser,
        env,
        brand,
        geoCountry,
    });
}

/**
 * Logs API call metrics to New Relic
 * @param {Object} params
 * @param {string} params.apiName - Name of the API being called
 * @param {string} params.apiUrl - Full URL of the API endpoint
 * @param {number} params.statusCode - HTTP status code
 * @param {number} params.durationMs - Duration in milliseconds
 * @param {*} [params.*] - Any extra attributes to log (e.g., input, errorMessage)
 */
export const logApiCall = ({
    apiName,
    apiUrl,
    statusCode,
    durationMs,
    ...additionalAttributes
}) => {
    const kenanId = window?.foxtel?.accountId || 'anonymous';
    const browser = detectBrowser().browser;
    const appVsBrowser = detectAppVsBrowser();
    const env = import.meta.env.VITE_BOT_ENV;
    const brand = 'foxtel';
    const geoCountry = 'AU';

    window.newrelic?.addPageAction('apiCall', {
        apiName,
        apiUrl,
        statusCode,
        durationMs,
        kenanId,
        browser,
        appVsBrowser,
        env,
        brand,
        geoCountry,
        ...additionalAttributes,
    });
};
